import React from "react"

import { TResolvedNewsSubpageItem } from "@/lib/kontent/pages/getNewsAggregator"
import { formatDate, formatReadTime } from "@/lib/utils"
import { CardNews } from "@/components/ui/molecules"

/**
 * @component
 *
 * `CardNewsResolver` is a resolver component designed for handling data from a CMS in page templates.
 *
 * You can pass the data received from the CMS to this component's props by spreading them, e.g., `<CardNewsResolver {...cmsData} />`.
 * The component then adjusts and maps the data as needed for the final module.
 *
 * Any logic related to additional data processing, such as Google Tag Manager's `dataLayer` or other SEO scripts, should be implemented here. This provides a centralized place for data transformation and treatment before rendering.
 *
 * @example
 * ```tsx
 * const cmsData = {
 *   id: '123',
 *   title: 'Sample News',
 *   urlSlug: '/sample-news'
 *   // ... other data
 * };
 *
 * <CardNewsResolver {...cmsData} />
 * ```
 *
 * @param {Object} data - The data from the CMS.
 * @returns {JSX.Element} A rendered `CardNews` component with the processed data.
 */

type TCardNewsResolverProps = {
  resolvedData: TResolvedNewsSubpageItem
  isH3?: boolean
  preview?: boolean
}

const CardNewsResolver = async ({
  resolvedData,
  isH3 = true,
  preview,
}: TCardNewsResolverProps) => {
  const imageSrc = resolvedData.src
    ? `${resolvedData.src}?w=2400&h=1600&fm=webp&q=90`
    : ""
  const link = resolvedData?.pageTree?.path
    ? `/${resolvedData?.pageTree?.path}`
    : ""

  const kontentData = {
    type: resolvedData.type,
    title: resolvedData.title,
    date: formatDate(resolvedData.publicationDate),
    readTime: formatReadTime(resolvedData.readingTime),
    description: resolvedData.introduction,
    link,
    src: imageSrc,
    alt: resolvedData.alt || `image ${resolvedData.title}`,
    tagLabel: resolvedData.theme.name,
    isH3: isH3,
  }
  return (
    <CardNews key={resolvedData.codename} {...kontentData} preview={preview} />
  )
}

export { CardNewsResolver, type TCardNewsResolverProps }
